import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { DiscountStatus } from '@/types';

export interface Discount {
  codeId: string;
  code: string;
  name: string;
  planTitle: string;
  priceId: string;
  productId?: string;
  discountPct: number;
  expiresAt: string;
  redeemedAt?: string | null;
  status: DiscountStatus;
  unitAmount?: number;
}

export interface DiscountGroup {
  codeId: string;
  code: string;
  discountPct: number;
  expiresAt: string;
  redeemedAt?: string | null;
  status: DiscountStatus;
  discounts: Discount[];
  planTitles: string[];
}

interface CapitalStore {
  discountGroups: DiscountGroup[];
  manualDiscountGroups: DiscountGroup[];
  syncDiscountGroups: (
    rewards: DiscountGroup[],
    promocodes: DiscountGroup[],
  ) => void;
  addManualDiscountGroup: (group: DiscountGroup) => void;
  removeDiscountGroup: (codeId: string) => void;
  updateDiscountGroup: (group: DiscountGroup) => void;

  appliedDiscountGroup: DiscountGroup | null;
  setAppliedDiscountGroup: (group: DiscountGroup | null) => void;

  clearCapitalStore: () => void;
}

export const useCapitalStore = create<CapitalStore>()(
  persist(
    (set) => ({
      discountGroups: [],
      manualDiscountGroups: [],
      syncDiscountGroups: (
        rewards: DiscountGroup[],
        promocodes: DiscountGroup[],
      ) =>
        set((state) => {
          const activeRewards = rewards.filter((group) =>
            group.discounts.every(isDiscountActive),
          );
          const activeManualDiscountGroups = state.manualDiscountGroups.filter(
            (group) => group.discounts.every(isDiscountActive),
          );
          const activePromocode = promocodes.filter((group) =>
            group.discounts.every(isDiscountActive),
          );
          const newDiscountGroups = [
            ...activeRewards,
            ...activeManualDiscountGroups,
            ...activePromocode,
          ].sort((a, b) => b.discountPct - a.discountPct);

          return {
            discountGroups: newDiscountGroups,
            manualDiscountGroups: activeManualDiscountGroups,
            appliedDiscountGroup: newDiscountGroups.find(
              (group) => group.codeId === state.appliedDiscountGroup?.codeId,
            )
              ? state.appliedDiscountGroup
              : null,
          };
        }),
      addManualDiscountGroup: (group: DiscountGroup) =>
        set((state) => {
          if (state.discountGroups.some((g) => g.codeId === group.codeId)) {
            return state;
          }

          return {
            discountGroups: [...state.discountGroups, group].sort(
              (a, b) => b.discountPct - a.discountPct,
            ),
            manualDiscountGroups: [...state.manualDiscountGroups, group],
          };
        }),
      removeDiscountGroup: (codeId: string) =>
        set((state) => ({
          discountGroups: state.discountGroups.filter(
            (g) => g.codeId !== codeId,
          ),
          manualDiscountGroups: state.manualDiscountGroups.filter(
            (g) => g.codeId !== codeId,
          ),
          appliedDiscountGroup:
            state.appliedDiscountGroup?.codeId === codeId
              ? null
              : state.appliedDiscountGroup,
        })),
      updateDiscountGroup: (group: DiscountGroup) =>
        set((state) => ({
          discountGroups: state.discountGroups.map((g) =>
            g.codeId === group.codeId ? group : g,
          ),
          manualDiscountGroups: state.manualDiscountGroups.map((g) =>
            g.codeId === group.codeId ? group : g,
          ),
          appliedDiscountGroup:
            state.appliedDiscountGroup?.codeId === group.codeId
              ? group
              : state.appliedDiscountGroup,
        })),

      appliedDiscountGroup: null,
      setAppliedDiscountGroup: (group: DiscountGroup | null) =>
        set((state) => {
          if (!group) {
            return { appliedDiscountGroup: null };
          }

          if (group.discounts.some((discount) => !isDiscountActive(discount))) {
            return {
              discountGroups: state.discountGroups.filter(
                (g) => g.codeId !== group.codeId,
              ),
              manualDiscountGroups: state.manualDiscountGroups.filter(
                (g) => g.codeId !== group.codeId,
              ),
              appliedDiscountGroup: null,
            };
          }

          return {
            appliedDiscountGroup: group,
          };
        }),

      clearCapitalStore: () =>
        set({
          discountGroups: [],
          manualDiscountGroups: [],
          appliedDiscountGroup: null,
        }),
    }),
    {
      name: 'capital',
      partialize: (state) => ({
        appliedDiscountGroup: state.appliedDiscountGroup,
        manualDiscountGroups: state.manualDiscountGroups,
      }),
    },
  ),
);

export const isDiscountActive = (discount?: Discount) => {
  if (!discount || discount.status !== DiscountStatus.ACTIVE) {
    return false;
  }

  const expiresAtMs = Date.parse(discount.expiresAt);

  if (Number.isNaN(expiresAtMs)) {
    return false;
  }

  return expiresAtMs > Date.now();
};
